import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SendIcon, PaperclipIcon, XIcon, CheckCircleIcon, ArrowLeftIcon, FileIcon, ImageIcon, FileTextIcon } from 'lucide-react';
type FeedbackCategory = 'general' | 'feature' | 'bug' | 'improvement' | 'other';
interface AttachedFile {
  name: string;
  size: number;
  type: string;
}
const categoryOptions: {
  value: FeedbackCategory;
  label: string;
  emoji: string;
}[] = [{
  value: 'general',
  label: 'General Feedback',
  emoji: '💬'
}, {
  value: 'feature',
  label: 'Feature Request',
  emoji: '✨'
}, {
  value: 'bug',
  label: 'Bug Report',
  emoji: '🐛'
}, {
  value: 'improvement',
  label: 'Improvement Suggestion',
  emoji: '💡'
}, {
  value: 'other',
  label: 'Other',
  emoji: '📝'
}];
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return <ImageIcon className="h-4 w-4" data-id="element-1727" />;
  if (type.includes('pdf') || type.includes('document')) return <FileTextIcon className="h-4 w-4" data-id="element-1728" />;
  return <FileIcon className="h-4 w-4" data-id="element-1729" />;
};
export const Feedback = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<FeedbackCategory>('general');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<AttachedFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<{
    title?: string;
    description?: string;
  }>({});
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newAttachments: AttachedFile[] = Array.from(files).map(file => ({
      name: file.name,
      size: file.size,
      type: file.type
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: {
      title?: string;
      description?: string;
    } = {};
    if (!title.trim()) newErrors.title = 'Please add a title';
    if (!description.trim()) newErrors.description = 'Please describe your feedback';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1500);
  };
  if (isSubmitted) {
    return <div className="min-h-[80vh] flex items-center justify-center px-4" data-id="element-1730">
        <div className="text-center max-w-md" data-id="element-1731">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1732">
            <CheckCircleIcon className="h-8 w-8 text-green-600" data-id="element-1733" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1734">
            Thanks for your feedback!
          </h1>
          <p className="text-gray-600 mb-8" data-id="element-1735">
            We've received your{' '}
            {categoryOptions.find(c => c.value === category)?.label.toLowerCase()}{' '}
            and will review it shortly. Your input helps us make RealUseAI
            better for everyone.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1736">
            <button onClick={() => {
            setIsSubmitted(false);
            setTitle('');
            setDescription('');
            setAttachments([]);
            setCategory('general');
          }} className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors" data-id="element-1737">
              Submit Another
            </button>
            <button onClick={() => navigate('/')} className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-blue-700 transition-all" data-id="element-1738">
              Back to Home
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="max-w-2xl mx-auto px-4 py-10" data-id="element-1739">
      {/* Back link */}
      <button onClick={() => navigate(-1)} className="inline-flex items-center text-gray-500 hover:text-gray-700 text-sm mb-8 transition-colors" data-id="element-1740">
        <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-1741" />
        Back
      </button>

      {/* Header */}
      <div className="mb-8" data-id="element-1742">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1743">Submit Feedback</h1>
        <p className="text-gray-500 mt-1.5" data-id="element-1744">
          Help us improve RealUseAI — we read every submission.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1745">
        {/* Category */}
        <div data-id="element-1746">
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1747">
            Category
          </label>
          <select id="category" value={category} onChange={e => setCategory(e.target.value as FeedbackCategory)} className="w-full rounded-lg border border-gray-300 py-3 px-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow bg-white" data-id="element-1748">
            {categoryOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1749">
                {opt.emoji} {opt.label}
              </option>)}
          </select>
        </div>

        {/* Title */}
        <div data-id="element-1750">
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1751">
            Title
          </label>
          <input id="title" type="text" value={title} onChange={e => {
          setTitle(e.target.value);
          if (errors.title) setErrors(prev => ({
            ...prev,
            title: undefined
          }));
        }} placeholder={category === 'bug' ? 'e.g. App crashes when uploading large images' : category === 'feature' ? 'e.g. Add dark mode support' : 'Give your feedback a short title'} className={`w-full rounded-lg border ${errors.title ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'} py-3 px-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow`} data-id="element-1752" />
          {errors.title && <p className="text-sm text-red-500 mt-1" data-id="element-1753">{errors.title}</p>}
        </div>

        {/* Description */}
        <div data-id="element-1754">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1755">
            Description
          </label>
          <textarea id="description" value={description} onChange={e => {
          setDescription(e.target.value);
          if (errors.description) setErrors(prev => ({
            ...prev,
            description: undefined
          }));
        }} rows={6} placeholder={category === 'bug' ? 'What happened? What did you expect to happen? Steps to reproduce...' : category === 'feature' ? "Describe the feature you'd like and how it would help you..." : "Tell us what's on your mind..."} className={`w-full rounded-lg border ${errors.description ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'} py-3 px-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow resize-none`} data-id="element-1756" />
          {errors.description && <p className="text-sm text-red-500 mt-1" data-id="element-1757">{errors.description}</p>}
          <p className="text-xs text-gray-400 mt-1.5 text-right" data-id="element-1758">
            {description.length} characters
          </p>
        </div>

        {/* Attachments */}
        <div data-id="element-1759">
          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1760">
            Attachments{' '}
            <span className="text-gray-400 font-normal" data-id="element-1761">(optional)</span>
          </label>

          {attachments.length > 0 && <div className="space-y-2 mb-3" data-id="element-1762">
              {attachments.map((file, index) => <div key={index} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5" data-id="element-1763">
                  <div className="flex items-center space-x-2.5 min-w-0" data-id="element-1764">
                    <div className="text-gray-400 flex-shrink-0" data-id="element-1765">
                      {getFileIcon(file.type)}
                    </div>
                    <span className="text-sm text-gray-700 truncate" data-id="element-1766">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-400 flex-shrink-0" data-id="element-1767">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                  <button type="button" onClick={() => removeAttachment(index)} className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 ml-2" aria-label="Remove attachment" data-id="element-1768">
                    <XIcon className="h-4 w-4" data-id="element-1769" />
                  </button>
                </div>)}
            </div>}

          <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center px-4 py-2.5 border border-gray-300 border-dashed rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/50 transition-all" data-id="element-1770">
            <PaperclipIcon className="h-4 w-4 mr-2" data-id="element-1771" />
            Add file
          </button>
          <input ref={fileInputRef} type="file" multiple onChange={handleFileChange} className="hidden" accept="image/*,.pdf,.doc,.docx,.txt,.zip" data-id="element-1772" />
          <p className="text-xs text-gray-400 mt-1.5" data-id="element-1773">
            Screenshots, logs, or documents — up to 10 MB each
          </p>
        </div>

        {/* Submit */}
        <div className="pt-2" data-id="element-1774">
          <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center" data-id="element-1775">
            {isSubmitting ? <div className="flex items-center space-x-2" data-id="element-1776">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1777" />
                <span data-id="element-1778">Submitting…</span>
              </div> : <div className="flex items-center space-x-2" data-id="element-1779">
                <SendIcon className="h-4 w-4" data-id="element-1780" />
                <span data-id="element-1781">Submit Feedback</span>
              </div>}
          </button>
        </div>
      </form>
    </div>;
};